import type {
  AdminReportSummary,
  AdminSubjectSummary,
  AdminSubscriptionList,
  AdminTopicResponse,
  AnswerAttachment,
  AttachmentUploadResponse,
  CostSummary,
  CostSummaryOverview,
  EnrolledSubject,
  GradeReportResponse,
  GradeResponse,
  GuidedQuestionRequest,
  HomeResponse,
  LearningProfileResponse,
  LlmUsageCallList,
  MockSubmitResponse,
  MockSessionResponse,
  PairingTokenLookupResponse,
  PairingTokenResponse,
  PairingTokenStatusResponse,
  ProgressResponse,
  QuestionResponse,
  RegradeResponse,
  ProductFeedbackRequest,
  ProductFeedbackResponse,
  ResponseFeedbackResponse,
  ResponseFeedbackTarget,
  ResponseRating,
  SessionAnalyticsResponse,
  SubjectSummary,
  SubscribeResponse,
  SubscriptionAnalyticsResponse,
  SubscriptionStatus,
  TutorMessageResponse,
  TutorSessionResponse,
  UnmappedTopicResponse,
} from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type { CarryOnResponse } from "./types";
import type { UserPreferences, UserPreferencesPatch } from "./types";
import type { FocusProfileResponse } from "./types";
import type {
  FocusBlockCloseRequest,
  FocusBlockCloseResponse,
  FocusBlockOpenRequest,
  FocusBlockOpenResponse,
} from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type {
  ConsentRecord,
  RegistrationRequest,
  RegistrationResponse,
} from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type {
  PayerSeat,
  SeatClaimDetails,
  SeatClaimResult,
  SeatInvitationCancelled,
  SeatInvitationIssued,
  SeatRevoked,
} from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type { BillingPortalSession } from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type {
  FundingCheckoutStarted,
  FundingRequestCreated,
  FundingRequestDeclined,
  FundingRequestDetails,
  FundingRequestWithdrawn,
  MyFundingRequest,
} from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type { PasswordResetResponse, VerificationEmailResponse } from "./types";
// Kept off the alphabetical block above so concurrent additions don't collide.
import type { ProductStatsReport } from "./types";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

/**
 * A refused API call.
 *
 * PRD §10's error envelope is FLAT — `{code, message, actions}` — and it
 * replaces FastAPI's `{"detail": ...}` for every refusal that carries a
 * reason code.
 *
 * `code` is the one carrier for "why this was refused", whichever envelope
 * the route answered with: §10's `code` where the route has adopted it, and
 * the plain `detail` string on the routes outside the §10 table — every 401
 * (§10 has no 401 row; the remedy is signalled by `WWW-Authenticate`), the
 * 400 `invalid_promo_code`, registration's own refusals, and the 429 rate
 * limits, whose `detail` is a sentence written for the user rather than a
 * code. One field, so a call site never has to know which of the two its
 * route used.
 *
 * Reason codes are additive and never renamed, so NO caller may switch
 * exhaustively: an unrecognised code is a generic failure with no actions.
 *
 * §10's `actions` is deliberately not modelled here, and that is a positive
 * choice rather than a gap: an affordance that vanishes because a server
 * omitted it from a list is worse than one chosen from the code. So the
 * surface that owns a flow routes to it on `code`, where the copy that
 * explains the refusal and the control that answers it are written together
 * and cannot drift apart — `resend_verification` from `email_not_verified`
 * and `request_payment` from `age_below_purchase_threshold`, both in
 * `SeatPurchase.applyRefusal`.
 *
 * `age_below_purchase_threshold` offers §9.5's learner-initiated funding
 * request (`rl-757.27`, `askForFunding` below) rather than an apology —
 * which matters more than its size suggests,
 * because §12.1's threshold is 16 and the cohort is 14-16, so for most of
 * the people this product is for it is the ONLY route to a seat.
 *
 * The remaining action values (`notify_payer`, `contact_payer`,
 * `request_new_invitation`, `manage_invitation`, `enrol`, `purchase_self`)
 * name flows of rl-757.26..29, and the same rule governs each: the surface
 * that owns the flow reads the `code`, and a code with nowhere to go is a
 * generic failure.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * `fetch` rejected before any HTTP response reached us: offline, DNS, a
 * dropped connection, or a response the browser withheld (a cross-origin
 * answer without CORS headers). Browsers word this differently — "Failed to
 * fetch", "NetworkError when attempting to fetch resource.", "Load failed" —
 * so the message is kept for operators while surfaces branch on the class
 * and write their own sentence (rl-gmq).
 */
export class NetworkError extends Error {
  constructor(readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "NetworkError";
  }
}

type TokenProvider = () => string | null;

let tokenProvider: TokenProvider = () => null;

export function setTokenProvider(fn: TokenProvider): void {
  tokenProvider = fn;
}

/**
 * Mint a fresh ID token, or null if nobody is signed in.
 *
 * Registered from the auth store for the same reason as {@link
 * setTokenProvider}: `auth/firebase.ts` imports this module, so importing it
 * back would be a cycle.
 */
export type TokenRefresher = () => Promise<string | null>;

let tokenRefresher: TokenRefresher = async () => null;
let tokenRefreshPromise: Promise<string | null> | null = null;

/**
 * Share one force-refresh across every request rejected by the same stale
 * credential. Firebase notifies session listeners while refreshing; those
 * listeners can issue API requests of their own, so calling the refresher
 * independently from each 401 creates a request -> refresh -> request loop.
 */
function refreshToken(): Promise<string | null> {
  if (tokenRefreshPromise) return tokenRefreshPromise;

  // Defer invoking the callback until after tokenRefreshPromise is assigned.
  // This also protects against a refresher with synchronous side effects.
  const refresh = Promise.resolve()
    .then(() => tokenRefresher())
    .catch(() => null);
  tokenRefreshPromise = refresh;
  void refresh.finally(() => {
    if (tokenRefreshPromise === refresh) tokenRefreshPromise = null;
  });
  return refresh;
}


export function setTokenRefresher(fn: TokenRefresher): void {
  tokenRefresher = fn;
  tokenRefreshPromise = null;
}

/**
 * Observe a final API refusal before it reaches the calling component.
 *
 * The router registers this without creating an api -> router import cycle.
 * It is presentation only: the server has already made the authorization
 * decision. The handler exists so a reason-coded entitlement revocation can
 * move a learner off a route that is no longer usable instead of leaving each
 * request on that page to render the same refusal.
 */
export type ApiRefusalHandler = (error: ApiError) => void | Promise<void>;

let apiRefusalHandler: ApiRefusalHandler = () => {};

export function setApiRefusalHandler(fn: ApiRefusalHandler): void {
  apiRefusalHandler = fn;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  responseType: "body" | "blob" = "body",
  /** Internal: set on the one retry after a token refresh. */
  isRetry = false,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (API_BASE_URL === "/api") {
    // Staging's same-origin Access gateway must answer an expired session
    // with 401, not redirect fetch to an HTML login page. Reloading the app
    // then runs the normal Access login flow.
    headers.set("X-Requested-With", "XMLHttpRequest");
  }
  headers.set("Accept", responseType === "blob" ? "text/csv" : "application/json");
  // Don't auto-set Content-Type for FormData — the browser must add
  // the multipart boundary itself, and an explicit "application/json"
  // would break the upload. JSON bodies still get the default.
  const isFormData = init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  // An Authorization set by the caller wins over the ambient provider.
  // `register()` in auth/firebase.ts needs that: it fires the
  // verification-email call with the token it has just minted for the
  // account it has just created, at a moment when the provider — which
  // reads the Pinia store, updated by the session listener — may not have
  // caught up yet. A silent 401 there would mean no verification mail for
  // a brand-new account, which is precisely the failure rl-4cu exists to
  // make impossible.
  if (!headers.has("Authorization")) {
    const token = tokenProvider();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  } catch (error) {
    throw new NetworkError(error);
  }

  // A 401 with `WWW-Authenticate` is §6.4's revocation-staleness answer:
  // this credential predates a permission removal, so force-mint one token
  // and retry once. Grant-only claim lag never reaches this path — Postgres
  // already honours a grant, and the server's separate revocation floor lets
  // the old token remain usable while the claims outbox catches up.
  //
  // ONCE, and only on a 401. A loop here would hammer the API with a
  // credential it has already rejected; `isRetry` is what makes the second
  // 401 final. A refresher that yields no token means nobody is signed in,
  // so there is nothing to retry with and the 401 stands.
  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshToken();
    if (refreshed) {
      const retryHeaders = new Headers(init.headers);
      retryHeaders.set("Authorization", `Bearer ${refreshed}`);
      return request<T>(
        path,
        { ...init, headers: retryHeaders },
        responseType,
        true,
      );
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (response.ok && responseType === "blob") {
    return (await response.blob()) as T;
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const envelope =
      body && typeof body === "object"
        ? (body as Record<string, unknown>)
        : null;
    // §10's flat body first, then the routes that answer with
    // FastAPI's. A route emits one or the other, never both.
    const code =
      typeof envelope?.code === "string"
        ? envelope.code
        : envelope && "detail" in envelope
          ? String(envelope.detail)
          : typeof body === "string" && body.length > 0
            ? body
            : response.statusText || undefined;
    // §10 writes a human sentence too, but a surface that owns a recovery
    // route writes its own: keyed on the code, localisable, and in
    // §11.2.4's case worded to leak nothing. So `message` is the fallback
    // for surfaces with no code-specific copy, and what an operator reads
    // in a log or a Sentry issue — never the wording of a refusal we have
    // deliberately chosen words for.
    const message =
      typeof envelope?.message === "string" && envelope.message.length > 0
        ? envelope.message
        : `API error ${response.status}`;
    const error = new ApiError(response.status, message, code);
    try {
      await apiRefusalHandler(error);
    } finally {
      // Navigation is an affordance, never a replacement for the server's
      // refusal. Even a broken handler must leave the caller the real error.
      throw error;
    }
  }

  return body as T;
}

export const apiClient = {
  health: () => request<{ status: string }>("/health"),
  listSubjects: (filters?: Record<string, string>) => {
    const params = new URLSearchParams(filters);
    const qs = params.toString();
    return request<SubjectSummary[]>(`/subjects${qs ? "?" + qs : ""}`);
  },
  getTaxonomy: () => request<Record<string, string[]>>("/subjects/taxonomy"),
  getSubject: (id: string) => request<SubjectSummary>(`/subjects/${id}`),
  getSubscription: () => request<SubscriptionStatus>("/me/subscription"),
  /**
   * Buy one seat (PRD §7.3, §7.7, §8.3). Named options rather than
   * positionals: both fields are optional and both are strings, so a call
   * site would otherwise be able to swap them silently.
   *
   * `holderEmail` answers "who is this seat for?" — omitted or null means
   * the buyer.
   *
   * `promoCode` is pre-applied server-side as a discount on THIS seat's
   * line item (§8.3), never on the subscription, so a payer funding several
   * seats can put a different code on each and still receive one invoice.
   * Pre-applying is also what lets `payment_method_collection: 'if_required'`
   * take effect — Stripe evaluates it at session creation, so a 100%-off
   * code has to be known before the redirect for a trial to be card-free.
   * No code is named here as an example: live Stripe holds no coupon at all
   * (rl-ef0).
   *
   * Refusals arrive as an `ApiError` whose `code` is the reason code
   * (`already_subscribed`, `holder_already_entitled`, `email_not_verified`,
   * `age_below_purchase_threshold`, `funding_requires_existing_subscription`,
   * `invalid_promo_code`) — SeatPurchase turns each into its own sentence,
   * and anything unrecognised into a generic failure.
   */
  subscribe: (
    options: {
      promoCode?: string | null;
      holderEmail?: string | null;
      seatFor?: "self" | "someone_else";
    } = {},
  ) =>
    request<SubscribeResponse>("/subscribe", {
      method: "POST",
      body: JSON.stringify({
        promo_code: options.promoCode ?? null,
        holder_email: options.holderEmail ?? null,
        // Sent rather than omitted: the server's default is the same, and
        // stating it keeps the intent visible in the one place a reader
        // goes looking for what the purchase surface asked for.
        seat_for: options.seatFor ?? "self",
      }),
    }),

  /**
   * The seat-claim round trip (PRD §9.4) and the payer's invitation
   * controls (§9.8, §11.3.5).
   *
   * ALL FIVE REQUIRE AUTHENTICATION, including the two that carry a claim
   * token. That is the §9.4 design and not an oversight: a claim link may
   * be forwarded or mistyped, so there is no unauthenticated variant to
   * disclose a funder, an entitlement, or even whether a token is real.
   * The claim page is static until the visitor signs in.
   *
   * `token` is a bearer credential and travels in `X-Seat-Claim-Token`,
   * never in the path and never in a body. Cloud Run logs request URLs and
   * nothing scrubs them, so a token in the path would be persisted in an
   * access log we would then have to keep clean; a header is not logged.
   * Nothing here may log it either, and the surfaces that call these
   * identify an invitation by seat id (§9.4).
   *
   * Refusals, all in §10's flat envelope except the rate limits:
   * - 410 `invitation_expired` for an expired token AND, identically, for
   *   one that is unknown, superseded, cancelled or presented by the wrong
   *   actor. Deliberately indistinguishable so probing learns nothing.
   * - 403 `pending_payer_confirmation` on POST when the authenticated
   *   address differs from the invited one (§11.2.3): consumption HAPPENED,
   *   activation waits on the payer. Not retryable.
   * - 403 `permission_denied` on the payer routes for an account that holds
   *   no `seat.manage` — the ordinary state of a self-serve learner.
   * - 404 on the payer routes, identical for "not your seat" and "no such
   *   seat", so probing ids learns nothing.
   * - 409 when the seat is no longer `pending_claim`, when there is no
   *   address to carry forward, and when the payer names their own address.
   * - 429 with FastAPI's `{"detail": ...}` and no reason code, per the
   *   repo's rate-limit convention — so the user-facing sentence arrives
   *   in `ApiError.code`, as it does for every other 429 here.
   */
  getSeatClaim: (token: string) =>
    request<SeatClaimDetails>("/me/claim", {
      headers: { "X-Seat-Claim-Token": token },
    }),
  acceptSeatClaim: (token: string) =>
    request<SeatClaimResult>("/me/claim", {
      method: "POST",
      headers: { "X-Seat-Claim-Token": token },
    }),
  getPayerSeats: () => request<PayerSeat[]>("/me/seats"),
  /**
   * Mint a claim link. `invitedEmail` is omitted for a plain re-issue (the
   * address carries forward from the seat's previous invitation) and
   * supplied to CORRECT a mistyped one (§9.8).
   *
   * Every call supersedes the seat's previous invitation and kills its
   * token immediately, so this is not idempotent in any sense the caller
   * may ignore: a second call invalidates the link the first one returned.
   */
  issueSeatInvitation: (seatId: string, invitedEmail?: string) =>
    request<SeatInvitationIssued>(`/me/seats/${seatId}/invitation`, {
      method: "POST",
      body: JSON.stringify(invitedEmail ? { invited_email: invitedEmail } : {}),
    }),
  cancelSeatInvitation: (seatId: string) =>
    request<SeatInvitationCancelled>(`/me/seats/${seatId}/invitation`, {
      method: "DELETE",
    }),

  /**
   * The payer's seat-lifecycle controls (PRD §8.5, §9.8, §11.2.6).
   *
   * Both are gated on `seat.manage` and scoped to what the caller FUNDS,
   * so another payer's seat is indistinguishable from one that does not
   * exist — a 404 either way, exactly as the invitation routes above.
   *
   * `reassignSeat` returns `SeatInvitationIssued` and not a shape of its
   * own, because a reassignment IS an issuance: the seat returns to
   * `pending_claim` with its holder cleared and a fresh invitation minted
   * and sent, by the same one path as a resend or a correction. The
   * `claim_url` matters because mail may not go out (rl-757.26), and the
   * copy-link is the path that works whatever the provider did.
   *
   * **Reassigning ends the current holder's access and kills any link
   * already handed over.** Any surface calling this must say so before the
   * button is pressed, not after.
   *
   * `revokeSeat` ends the seat and releases its funding. The money moves
   * through the outbox (rl-757.26), never inline, so this returns as soon
   * as the seat is terminal and a credit or refund follows out of band.
   * **It retains the holder's study data** (§11.2.6) — an entitlement
   * change is not a data-subject event — and that must be stated to the
   * payer alongside the fact that the charge stops, because a payer who
   * believes the first will not press it and one who doubts the second
   * will press it twice.
   */
  reassignSeat: (seatId: string, holderEmail: string) =>
    request<SeatInvitationIssued>(`/me/seats/${seatId}/reassign`, {
      method: "POST",
      body: JSON.stringify({ holder_email: holderEmail }),
    }),
  revokeSeat: (seatId: string) =>
    request<SeatRevoked>(`/me/seats/${seatId}/revoke`, { method: "POST" }),

  /**
   * A Stripe customer portal session (PRD §8.5).
   *
   * The `url` is single-use and short-lived, so the only correct thing to
   * do with it is redirect immediately; storing it or rendering it as a
   * link produces a dead one.
   *
   * §8.5's table is the boundary: the portal answers cancel, card and
   * invoices, and has NO representation for seats, holders or invitations,
   * which are ours. A surface sending a payer here must not imply
   * otherwise — the seat controls are `reassignSeat`, `revokeSeat` and the
   * invitation routes above, and the first support ticket §8.5 predicts is
   * "I paid for the wrong child's email address".
   *
   * Refuses with 409 `no_billing_account` for a caller who funds nothing,
   * so has no Stripe customer to open a portal against.
   */
  createBillingPortalSession: () =>
    request<BillingPortalSession>("/me/billing-portal", { method: "POST" }),

  /**
   * §9.5 — the learner asks someone to pay, and the payer's decision.
   *
   * This is the route to a seat for MOST OF THE COHORT: §12.1's
   * self-purchase threshold is 16 and the product is for 14-16s, so the
   * majority of learners cannot buy at all and `POST /subscribe` refuses
   * them with `age_below_purchase_threshold`.
   *
   * **Nothing billable exists until the payer pays** (§7.3). A funding
   * request is not a seat, not an agreement and not a Stripe object; the
   * checkout below is what creates all three, through the webhook.
   *
   * THE THREE PAYER ROUTES REQUIRE AUTHENTICATION, exactly as §9.4's claim
   * routes do and for the same reason: the learner types the address
   * themselves and may mistype it, and a link may be forwarded, so there is
   * no unauthenticated variant to disclose a child's name or even whether a
   * token is real. The funding page is static until the visitor signs in.
   *
   * `token` is a bearer credential and travels in `X-Funding-Request-Token`,
   * never in the path and never in a body — Cloud Run logs request URLs and
   * nothing scrubs them, so a token in a path would be persisted in an
   * access log we would then have to keep clean. Nothing here may log it
   * either.
   *
   * **THIS ROUTE'S REFUSALS CARRY NO §10 REASON CODE.** The 409s answer
   * with a plain `detail` sentence — one for an address already asked, one
   * naming the date the 7-day cooldown ends — and that is deliberate on the
   * server's side twice over: a §10 code is permanent and never renamed, so
   * a tunable window has no business behind one, and the nearest existing
   * code (`holder_already_invited`) ships the action `manage_invitation`,
   * which would point a 14-year-old at the payer's seat-management surface.
   * `ApiError.code` therefore carries the sentence itself here, as it does
   * for every 429 in this file, and a caller must RENDER IT rather than
   * switch on it.
   *
   * The 429 is the repo's usual shape, with `Retry-After`. That limit is
   * not incidental: this endpoint mails an ARBITRARY ADDRESS on a cheap
   * trigger (§11.1.5), so it is bounded per learner and per target address,
   * on top of the one-pending and cooldown rules the row itself enforces.
   */
  askForFunding: (payerEmail: string) =>
    request<FundingRequestCreated>("/me/funding-requests", {
      method: "POST",
      body: JSON.stringify({ payer_email: payerEmail }),
    }),
  /**
   * The learner's own asks, in the NEUTRAL projection §9.5 requires: the
   * server reports a declined request as `not_funded` with a
   * `can_ask_again_at` and never as a rejection, and no state here is
   * spelled like the column it came from. See `FundingRequestState`.
   */
  listMyFundingRequests: () =>
    request<MyFundingRequest[]>("/me/funding-requests"),
  /**
   * Take an ask back. Answers a body rather than a 204 (§11.3.5), and
   * `nothing_to_withdraw` covers another learner's id and a uuid that never
   * existed as well as this learner's absent one — identically, so a caller
   * that distinguished them would build the id oracle the server declined
   * to be.
   */
  withdrawFundingRequest: (id: string) =>
    request<FundingRequestWithdrawn>(`/me/funding-requests/${id}`, {
      method: "DELETE",
    }),
  getFundingRequest: (token: string) =>
    request<FundingRequestDetails>("/me/funding-request", {
      headers: { "X-Funding-Request-Token": token },
    }),
  declineFundingRequest: (token: string) =>
    request<FundingRequestDeclined>("/me/funding-request/decline", {
      method: "POST",
      headers: { "X-Funding-Request-Token": token },
    }),
  /**
   * The accept. There is no route spelled "accept" because accepting IS
   * paying (§7.3).
   *
   * Two outcomes, discriminated on `mode` (see `FundingCheckoutStarted`).
   * `checkout` opens Stripe carrying the request id in session metadata,
   * and the webhook creates the seat — held by the REQUESTER, who is
   * already known — and marks the request accepted in the same
   * transaction. `seat_added` is §7.7's path for a payer who already funds
   * somebody: one more item on the same subscription, so §9.3's one charge
   * survives a second child, and the seat exists when this returns.
   *
   * A caller that assumes the first and redirects on a null `url` turns a
   * successful purchase into "could not open checkout", which is why the
   * response is a union and not a bag of optionals.
   */
  startFundingCheckout: (token: string) =>
    request<FundingCheckoutStarted>("/me/funding-request/checkout", {
      method: "POST",
      headers: { "X-Funding-Request-Token": token },
    }),

  getMySubjects: () => request<EnrolledSubject[]>("/me/subjects"),
  enrollSubject: (subjectId: string) =>
    request<void>(`/subjects/${subjectId}/enroll`, { method: "POST" }),
  unenrollSubject: (subjectId: string) =>
    request<void>(`/subjects/${subjectId}/unenroll`, { method: "POST" }),
  generateQuestion: (subjectId: string, prompt: string) =>
    request<QuestionResponse>(`/subjects/${subjectId}/questions`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }),
  submitAnswer: (
    interactionId: string,
    answerText: string,
    attachments: AnswerAttachment[] = [],
  ) =>
    request<GradeResponse>(`/interactions/${interactionId}/answer`, {
      method: "POST",
      body: JSON.stringify({ answer_text: answerText, attachments }),
    }),
  // Re-photograph + re-grade. The backend
  // creates a fresh interaction row pointing at the original; the
  // 4xx path tells the UI when the per-course soft cap is hit.
  regradeAnswer: (
    interactionId: string,
    answerText: string,
    attachments: AnswerAttachment[] = [],
  ) =>
    request<RegradeResponse>(`/interactions/${interactionId}/regrade`, {
      method: "POST",
      body: JSON.stringify({ answer_text: answerText, attachments }),
    }),
  reportGrade: (interactionId: string, reason: string | null) =>
    request<GradeReportResponse>(`/interactions/${interactionId}/report`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  // Per-response feedback (tutor-uox.1 + tutor-uox.1.1). Idempotent on
  // (user, target): submitting again overwrites the prior row. Target is
  // either an interaction (practice / mock) or a chat turn (tutor).
  submitResponseFeedback: (
    target: ResponseFeedbackTarget,
    rating: ResponseRating,
    comment: string | null,
  ) =>
    request<ResponseFeedbackResponse>("/feedback/response", {
      method: "POST",
      body: JSON.stringify({ ...target, rating, comment }),
    }),

  // General product feedback (tutor-uox.2.a). Append-only — every call
  // creates a new row in product_feedback and pings the configured
  // Slack channel. Body is free-text PII; never log or send to analytics.
  submitGeneralFeedback: (body: ProductFeedbackRequest) =>
    request<ProductFeedbackResponse>("/feedback/general", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Home dashboard
  getHome: () => request<HomeResponse>("/me/home"),

  // Learning profile
  getLearningProfile: (subjectId: string) =>
    request<LearningProfileResponse>(`/subjects/${subjectId}/learning-profile`),
  generateGuidedQuestion: (subjectId: string, body: GuidedQuestionRequest) =>
    request<QuestionResponse>(`/subjects/${subjectId}/guided-question`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Session endpoints
  startPracticeSession: (subjectId: string) =>
    request<{ session_id: string }>(`/subjects/${subjectId}/practice-session`, {
      method: "POST",
    }),
  startTutorSession: (
    subjectId: string,
    body: { topic_id?: string; question_type?: string; marks?: number },
  ) =>
    request<TutorSessionResponse>(`/subjects/${subjectId}/tutor-session`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  sendTutorMessage: (sessionId: string, content: string) =>
    request<TutorMessageResponse>(`/sessions/${sessionId}/message`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  endSession: (sessionId: string) =>
    request<void>(`/sessions/${sessionId}/end`, { method: "POST" }),
  startMockSession: (
    subjectId: string,
    body: {
      num_questions: number;
      topic_ids?: string[];
      time_limit_minutes?: number;
    },
  ) =>
    request<MockSessionResponse>(`/subjects/${subjectId}/mock`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  submitMock: (
    sessionId: string,
    answers: { interaction_id: string; answer_text: string }[],
  ) =>
    request<MockSubmitResponse>(`/sessions/${sessionId}/submit-mock`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    }),

  // Progress
  getProgress: (subjectId: string) =>
    request<ProgressResponse>(`/subjects/${subjectId}/progress`),

  // Phone pairing (PRD §6.7)
  createPairingToken: (sessionId: string) =>
    request<PairingTokenResponse>("/pairing-tokens", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    }),
  getPairingTokenStatus: (token: string) =>
    request<PairingTokenStatusResponse>(`/pairing-tokens/${token}`),
  lookupPairingTokenByCode: (shortCode: string) =>
    request<PairingTokenLookupResponse>("/pairing-tokens/lookup", {
      method: "POST",
      body: JSON.stringify({ short_code: shortCode }),
    }),
  uploadAttachmentViaPairing: (token: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    // FormData sets its own Content-Type with boundary — let the
    // browser handle it. The bearer-token branch in request() will
    // happily forward it (and harmlessly attach Authorization, which
    // the public phone endpoint ignores).
    return request<AttachmentUploadResponse>(
      `/pairing-tokens/${token}/attachment`,
      {
        method: "POST",
        body: form,
      },
    );
  },

  // Admin endpoints
  adminListSubjects: () => request<AdminSubjectSummary[]>("/admin/subjects"),
  adminListSubscriptions: (
    { page = 1, pageSize = 50 }: { page?: number; pageSize?: number } = {},
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    return request<AdminSubscriptionList>(`/admin/subscriptions?${params}`);
  },
  adminExportSubscriptions: (userIds: string[]): Promise<Blob> =>
    request<Blob>(
      "/admin/subscriptions.csv",
      { method: "POST", body: JSON.stringify({ user_ids: userIds }) },
      "blob",
    ),
  // tutor-70k.9 — admin-only Sentry health probe. Always returns
  // 500; the SPA catches that and surfaces a confirmation toast.
  adminDebugSentryThrow: () =>
    request<void>("/admin/debug/sentry-throw", { method: "POST" }),

  // Admin analytics
  adminGetCostAnalytics: (days?: number, subjectId?: string) => {
    const params = new URLSearchParams();
    if (days) params.set("days", String(days));
    if (subjectId) params.set("subject_id", subjectId);
    const qs = params.toString();
    return request<CostSummary[]>(`/admin/analytics/cost${qs ? "?" + qs : ""}`);
  },
  adminGetCostSummary: (days = 30, limit = 20) => {
    const qs = new URLSearchParams({
      days: String(days),
      limit: String(limit),
    }).toString();
    return request<CostSummaryOverview>(`/admin/analytics/cost/summary?${qs}`);
  },
  adminGetCostCalls: (
    opts: {
      days?: number;
      courseId?: string;
      callType?: "generate" | "grade" | "narrative";
      sort?: "recent" | "cost" | "cache_misses";
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.days) params.set("days", String(opts.days));
    if (opts.courseId) params.set("course_id", opts.courseId);
    if (opts.callType) params.set("call_type", opts.callType);
    if (opts.sort) params.set("sort", opts.sort);
    if (opts.page) params.set("page", String(opts.page));
    if (opts.pageSize) params.set("page_size", String(opts.pageSize));
    const qs = params.toString();
    return request<LlmUsageCallList>(
      `/admin/analytics/cost/calls${qs ? "?" + qs : ""}`,
    );
  },
  adminListReports: (pendingOnly = true) =>
    request<AdminReportSummary[]>(`/admin/reports?pending_only=${pendingOnly}`),
  adminReviewReport: (
    reportId: string,
    body: {
      corrected_marks?: number | null;
      notes?: string | null;
      add_to_holdout?: boolean;
    },
  ) =>
    request<{ status: string }>(`/admin/reports/${reportId}/review`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  adminUpdateQuotas: (
    subjectId: string,
    body: { daily_question_cap?: number; monthly_question_cap?: number },
  ) =>
    request<{ daily_question_cap: number; monthly_question_cap: number }>(
      `/admin/subjects/${subjectId}/quotas`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    ),

  // Admin topic tree (tutor-239.8.1)
  adminListTopics: (subjectId: string) =>
    request<AdminTopicResponse[]>(`/admin/subjects/${subjectId}/topics`),
  adminCreateTopic: (
    subjectId: string,
    body: {
      slug: string;
      display_name: string;
      parent_id?: string | null;
      sort_order?: number;
      spec_reference?: string | null;
      exam_weighting_pct?: number | null;
      expected_question_types?: string[] | null;
    },
  ) =>
    request<AdminTopicResponse>(`/admin/subjects/${subjectId}/topics`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  adminUpdateTopic: (
    subjectId: string,
    topicId: string,
    body: {
      display_name?: string;
      slug?: string;
      sort_order?: number;
      spec_reference?: string | null;
      exam_weighting_pct?: number | null;
      expected_question_types?: string[] | null;
    },
  ) =>
    request<AdminTopicResponse>(
      `/admin/subjects/${subjectId}/topics/${topicId}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    ),
  adminDeleteTopic: (subjectId: string, topicId: string) =>
    request<void>(`/admin/subjects/${subjectId}/topics/${topicId}`, {
      method: "DELETE",
    }),

  // Admin topic mapping review queue (tutor-239.8.2)
  adminListUnmappedTopics: (courseId?: string) => {
    const params = new URLSearchParams();
    if (courseId) params.set("course_id", courseId);
    const qs = params.toString();
    return request<UnmappedTopicResponse[]>(
      `/admin/topic-mappings/unmapped${qs ? "?" + qs : ""}`,
    );
  },
  adminCreateMapping: (body: {
    course_id: string;
    raw_topic: string;
    course_topic_id: string;
  }) =>
    request<{ status: string; backfill_count: string }>(
      "/admin/topic-mappings",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),

  // Admin session & subscription analytics (tutor-239.8.3)
  adminGetSessionAnalytics: (days?: number) => {
    const params = new URLSearchParams();
    if (days) params.set("days", String(days));
    const qs = params.toString();
    return request<SessionAnalyticsResponse>(
      `/admin/analytics/sessions${qs ? "?" + qs : ""}`,
    );
  },
  adminGetSubscriptionAnalytics: () =>
    request<SubscriptionAnalyticsResponse>("/admin/analytics/subscriptions"),
  // Registration-week cohort report (rl-5qhy). Omitted dates use the API's
  // default window: the current ISO week plus the eleven before it.
  adminGetProductStats: ({ from, to }: { from?: string; to?: string } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();
    return request<ProductStatsReport>(`/admin/product-stats${qs ? "?" + qs : ""}`);
  },

  // Today's cross-subject suggestion (D5). Answers `null` — not a 404 — when
  // there is nothing worth suggesting, so the return type is nullable.
  getCarryOn: () => request<CarryOnResponse | null>("/me/carry-on"),

  // Account-page study defaults (D8). GET never 404s — a user with no stored
  // row gets the documented defaults. PATCH takes a partial body, upserts it,
  // and returns the FULL updated row so callers reconcile without a re-GET.
  getPreferences: () => request<UserPreferences>("/me/preferences"),
  updatePreferences: (patch: UserPreferencesPatch) =>
    request<UserPreferences>("/me/preferences", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // Focus capacity, stage ladder and the 14-day sparkline (D4). Deliberately
  // separate from getHome: the Focus card is about the student, not a subject,
  // and it re-fetches after every completed block while Today does not.
  getFocusProfile: () => request<FocusProfileResponse>("/me/focus-profile"),

  // Focus block write path (contract D2). The only producers of `focus_blocks`
  // rows, which are the only substrate for `capacity_minutes` — nothing here
  // can be reconstructed later.
  openFocusBlock: (body: FocusBlockOpenRequest) =>
    request<FocusBlockOpenResponse>("/focus-blocks", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // `keepalive` lets the final PATCH outlive the page: a block abandoned by
  // closing the tab is otherwise lost silently. `sendBeacon` cannot carry the
  // Authorization header this endpoint requires, so a keepalive fetch is the
  // only unload-safe option.
  closeFocusBlock: (
    blockId: string,
    body: FocusBlockCloseRequest,
    keepalive = false,
  ) =>
    request<FocusBlockCloseResponse>(`/focus-blocks/${blockId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
      keepalive,
    }),
  // Flips TutorSession.status from 'active' to 'completed'. Best-effort at
  // every call site: a failed status update must never block a student from
  // closing the overlay.
  endSessionKeepalive: (sessionId: string) =>
    request<void>(`/sessions/${sessionId}/end`, {
      method: "POST",
      keepalive: true,
    }),

  // Registration (§9.1). Called AFTER the Identity Platform user exists —
  // this endpoint authenticates with that user's ID token and creates the
  // Postgres row, the `learner` grant and the first-touch attribution row.
  // Idempotent: a retried or double-submitted form gets the same account
  // back with `created: false` rather than a conflict.
  register: (body: RegistrationRequest) =>
    request<RegistrationResponse>("/register", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Consent per purpose (contract A8/A25). Returns rows for EVERY policy
  // version, superseded ones included, so callers must decide liveness
  // from `withdrawn_at` plus `policy_version` — see `ConsentRecord`.
  getConsents: () => request<ConsentRecord[]>("/me/consents"),
  // Idempotent: re-giving a live consent is a no-op, not a second row.
  // The server 409s `policy_version_stale` on any version but its own
  // current one, which is the whole reason the version is in the body —
  // it pins the recorded consent to the wording the client rendered.
  giveConsent: (purpose: string, policyVersion: string) =>
    request<void>("/me/consents", {
      method: "POST",
      body: JSON.stringify({ purpose, policy_version: policyVersion }),
    }),
  // Withdrawal writes `withdrawn_at` and is idempotent. Deliberately as
  // cheap to call as `giveConsent` — A25 requires withdrawal to be as easy
  // as the opt-in, and that starts with it being one request from the same
  // control rather than a support ticket.
  withdrawConsent: (purpose: string) =>
    request<void>(`/me/consents/${purpose}`, { method: "DELETE" }),

  /**
   * Auth mail (rl-4cu), sent by our API rather than the Firebase client
   * SDK's `sendEmailVerification` and `sendPasswordResetEmail`, which hand
   * the send to Identity Platform and leave us outside the SMTP
   * conversation: `accounts:sendOobCode` can answer 200 for mail the relay
   * refuses with `550`. We mint the link with the Admin SDK and send it
   * ourselves, so a refusal is an error in our own
   * process — logged and alerted there, not here.
   *
   * `idToken` on the verification call is for the one caller that cannot use
   * the ambient provider; see the Authorization note in `request` above.
   * Everyone else omits it.
   */
  sendVerificationEmail: (idToken?: string) =>
    request<VerificationEmailResponse>("/me/verification-email", {
      method: "POST",
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined,
    }),
  /**
   * Unauthenticated, and the dangerous one: it mails an address supplied by
   * whoever asked. Non-disclosure is the server's property — it answers
   * an identical 200 for a registered address, an unregistered one, a
   * rate-limited caller and a failed send (see `PasswordResetResponse`). So
   * there is nothing here to branch on, and a rejected call means the API
   * is unreachable or broken rather than anything about the address.
   */
  requestPasswordReset: (email: string) =>
    request<PasswordResetResponse>("/auth/password-reset", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
};
